import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Copy, Plus, Power, Trash2, Loader2 } from "lucide-react";
import { useBetaCodes, useCreateBetaCode, useDeactivateBetaCode, useDeleteBetaCode, useBetaCodeStats } from "@/hooks/useBetaCodes";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

export function BetaCodesTab() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newCode, setNewCode] = useState({
    code: "",
    label: "",
    max_uses: "",
    expires_at: "",
  });

  const { data: codes, isLoading } = useBetaCodes();
  const { data: stats } = useBetaCodeStats();
  const createMutation = useCreateBetaCode();
  const deactivateMutation = useDeactivateBetaCode();
  const deleteMutation = useDeleteBetaCode();
  const { toast } = useToast();

  const generateCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 12; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
      if (i === 5) code += "-";
    }
    setNewCode({ ...newCode, code });
  };

  const handleCreate = async () => {
    if (!newCode.code || !newCode.label) {
      toast({
        title: "Missing fields",
        description: "Code and label are required.",
        variant: "destructive",
      });
      return;
    }

    await createMutation.mutateAsync({
      code: newCode.code,
      label: newCode.label,
      max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
      expires_at: newCode.expires_at || null,
    });

    setIsCreateOpen(false);
    setNewCode({ code: "", label: "", max_uses: "", expires_at: "" });
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied!",
      description: "Beta code copied to clipboard.",
    });
  };

  const getStatusBadge = (code: any) => {
    if (!code.is_active) {
      return <Badge variant="outline">Inactive</Badge>;
    }
    if (code.expires_at && new Date(code.expires_at) < new Date()) {
      return <Badge variant="destructive">Expired</Badge>;
    }
    if (code.max_uses && code.current_uses >= code.max_uses) {
      return <Badge variant="destructive">Maxed Out</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  const getUsageText = (code: any) => {
    if (code.max_uses) {
      return `${code.current_uses}/${code.max_uses}`;
    }
    return `${code.current_uses}/∞`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active Codes</CardDescription>
            <CardTitle className="text-3xl">{stats?.totalActiveCodes || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Signups</CardDescription>
            <CardTitle className="text-3xl">{stats?.totalSignups || 0}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Most Used Code</CardDescription>
            <CardTitle className="text-3xl">{stats?.mostUsedCodeUses || 0} uses</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Beta Codes Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Beta Access Codes</CardTitle>
              <CardDescription>Manage your evergreen beta signup codes</CardDescription>
            </div>
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Beta Access Code</DialogTitle>
                  <DialogDescription>
                    Generate a new code for beta signups
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <div className="flex gap-2">
                      <Input
                        id="code"
                        placeholder="WINERY2025"
                        value={newCode.code}
                        onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                        className="uppercase"
                      />
                      <Button type="button" variant="outline" onClick={generateCode}>
                        Generate
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="label">Label</Label>
                    <Input
                      id="label"
                      placeholder="Harvest Season Access"
                      value={newCode.label}
                      onChange={(e) => setNewCode({ ...newCode, label: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="max_uses">Max Uses (optional)</Label>
                    <Input
                      id="max_uses"
                      type="number"
                      placeholder="Unlimited"
                      value={newCode.max_uses}
                      onChange={(e) => setNewCode({ ...newCode, max_uses: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expires_at">Expiration Date (optional)</Label>
                    <Input
                      id="expires_at"
                      type="datetime-local"
                      value={newCode.expires_at}
                      onChange={(e) => setNewCode({ ...newCode, expires_at: e.target.value })}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreate} disabled={createMutation.isPending}>
                    {createMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Code"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Uses</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {codes?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No beta codes yet. Create one to get started.
                  </TableCell>
                </TableRow>
              ) : (
                codes?.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono font-medium">
                      <div className="flex items-center gap-2">
                        {code.code}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyCode(code.code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>{code.label}</TableCell>
                    <TableCell>{getUsageText(code)}</TableCell>
                    <TableCell>{getStatusBadge(code)}</TableCell>
                    <TableCell>
                      {code.expires_at
                        ? format(new Date(code.expires_at), "MMM d, yyyy")
                        : "Never"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Power className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Deactivate code?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will prevent new signups with this code.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deactivateMutation.mutate(code.id)}
                              >
                                Deactivate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete code?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(code.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
